import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  InputSignal,
  computed,
  contentChild,
  Signal
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { IdService } from '../../services/id.service';
import { FocusAnchorComponent } from '../focus/focus-anchor.component';
import { NotificationIconDirective } from './notification-icon.directive';
import { NotificationHeaderComponent } from './notification-header.component';
import { NotificationCloseButtonComponent } from './notification-close-button.component';

export type NotificationType = 'warning' | 'success' | 'error' | 'info' | 'note';

export type NotificationNoteColour = 'grey' | 'tint' | 'white';

export type NotificationSize = 'regular' | 'compact' | 'full-width';

@Component({
  selector: 'td-wb-notification',
  templateUrl: 'notification.component.html',
  styleUrls: ['notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, FocusAnchorComponent, IconComponent],
  host: {
    '[attr.id]': 'id',
    '[class.td-wb-notification--compact]': 'notificationSize() === "compact"',
    '[class.td-wb-notification--full-width]': 'notificationSize() === "full-width"',
    '[class.td-wb-notification--warning]': 'notificationType() === "warning"',
    '[class.td-wb-notification--info]': 'notificationType() === "info"',
    '[class.td-wb-notification--error]': 'notificationType() === "error"',
    '[class.td-wb-notification--success]': 'notificationType() === "success"',
    '[class.td-wb-notification--note]': 'notificationType() === "note"',
    '[class.td-wb-notification--note--grey]': 'notificationType() === "note" && (notificationNoteColour() === undefined || notificationNoteColour() === "grey")',
    '[class.td-wb-notification--note--tint]': 'notificationType() === "note" && notificationNoteColour() === "tint"',
    '[class.td-wb-notification--note--white]': 'notificationType() === "note" && notificationNoteColour() === "white"'
  }
})
export class NotificationComponent {
  private static readonly ID_PREFIX: string = 'td-wb-notification';

  readonly focusOnInit: InputSignal<boolean | undefined> = input<boolean | undefined>(undefined);

  readonly notificationSize: InputSignal<NotificationSize> = input<NotificationSize>('compact');
  readonly notificationType: InputSignal<NotificationType> = input<NotificationType>('warning');
  readonly notificationNoteColour: InputSignal<NotificationNoteColour> = input<NotificationNoteColour>('grey');
  readonly showIcon: InputSignal<boolean> = input<boolean>(true);

  readonly header: Signal<NotificationHeaderComponent | undefined> = contentChild(NotificationHeaderComponent);
  readonly customIcon: Signal<NotificationIconDirective | undefined> = contentChild(NotificationIconDirective);
  readonly closeButton: Signal<NotificationCloseButtonComponent | undefined>  = contentChild(NotificationCloseButtonComponent);

  private readonly idService: IdService = inject(IdService);
  readonly id: string = this.idService.generateNamespaceSequentialId(NotificationComponent.ID_PREFIX);

  readonly iconId: Signal<string> = computed<string>(() => {
    if (this.notificationType() === 'warning') {
      return 'warning';
    }
    if (this.notificationType() === 'success') {
      return 'approved';
    }
    if (this.notificationType() === 'info') {
      return 'information';
    }
    if (this.notificationType() === 'note') {
      return 'info--small';
    }
    return 'error';
  });

  readonly isFocusOnInit: Signal<boolean> = computed(() => {
    const focus = this.focusOnInit();
    return focus !== undefined ? focus : this.notificationType() === 'error';
  });
}
